import { useQuery } from "@apollo/client";
import { Box, Stack, Tooltip, Typography, useTheme } from "@mui/material";
import { CopyableText } from "components/CopyableText";
import { FormSelect } from "components/Forms/FormSelect";
import { TruncatedText } from "components/Forms/TruncatedText";
import { TruncateTypography } from "components/TruncateTypography";
import { gql } from "generated-graphql";
import { CredentialInputForValidation, CredentialKind } from "generated-graphql/graphql";
import { useMemo } from "react";
import { Control, useWatch } from "react-hook-form";

const GET_PORTALS = gql(`
  query GetPortals($search: String, $sort: [SortModel!]) {
    portalSearch(search: $search, sort: $sort) {
      items {
        id
        jurisdiction
        description
        url
      }
    }
  }
`);

type CredentialPortalPickerProps = {
  control: Control<CredentialInputForValidation>;
  facilityState?: string | null;
  kind?: CredentialKind | null;
  disabled?: boolean;
};

export const CredentialPortalPicker = ({
  kind,
  disabled,
  control,
}: CredentialPortalPickerProps) => {
  const theme = useTheme();
  const { data } = useQuery(GET_PORTALS, {
    variables: {
      search: kind ? `kind:${kind}` : undefined,
      sort: [{ field: "description", sort: "asc" }],
    },
  });

  const portals = useMemo(() => data?.portalSearch?.items ?? [], [data]);

  const portalId = useWatch({ control, name: "portalId" });
  const portalUrl = useMemo(() => portals.find((p) => p.id === portalId)?.url, [portals, portalId]);

  return (
    <Stack>
      <FormSelect
        sx={{ mt: 0.5 }}
        disabled={disabled}
        name="portalId"
        label="Portal"
        selectItems={portals.map((p) => ({
          display: p.description ?? p.jurisdiction,
          value: p.id,
        }))}
        control={control}
        rules={{ required: true }}
      />

      {portalUrl ? (
        <Box paddingBottom={theme.spacing(2)} display="flex" flexWrap="nowrap">
          <Typography fontWeight="bold" paddingRight={theme.spacing(0.5)}>
            URL:
          </Typography>
          <TruncateTypography
            showTooltip
            sx={{ maxWidth: theme.spacing(50), paddingRight: theme.spacing(0.5) }}
          >
            {portalUrl}
          </TruncateTypography>
          <CopyableText hideText>{portalUrl}</CopyableText>
        </Box>
      ) : null}
    </Stack>
  );
};
